import {
  createGrid,
  GridApi,
  GridOptions,
  ColDef,
  IRowNode,
  SelectionChangedEvent,
  ProcessCellForExportParams,
} from "ag-grid-enterprise";
import { AgGridBaseController } from "../ag_grid_base_component/ag_grid_base_controller";
import { StatusCellRenderer } from "../ag_grid_shared/cell_renderers/status_cell_renderer";
import { ActionsCellRenderer } from "../ag_grid_shared/cell_renderers/actions_cell_renderer";
import { ebTheme } from "../ag_grid_shared/eb_theme";
import { formatCellValue } from "../ag_grid_shared/export_utils";

export default class extends AgGridBaseController {
  static targets = [
    ...AgGridBaseController.targets,
    "quickFilterInput",
  ];

  static values = {
    ...AgGridBaseController.values,
    rowDataUrl:           String,
    rowData:              Array,
    advancedRowDataUrl:   String,
    basicModeOptions:     Object,
    advancedModeOptions:  Object,
    followSelection:      Boolean,
    footerCounts:         Object,
  };

  declare readonly quickFilterInputTarget: HTMLInputElement;
  declare readonly hasQuickFilterInputTarget: boolean;

  declare readonly rowDataUrlValue: string;
  declare readonly rowDataValue: Record<string, unknown>[];
  declare readonly advancedRowDataUrlValue: string;
  declare readonly basicModeOptionsValue: Partial<GridOptions>;
  declare readonly advancedModeOptionsValue: Partial<GridOptions>;
  declare readonly followSelectionValue: boolean;
  declare readonly footerCountsValue: {
    labelField?: string;
    sumFields?: string[];
    countLabel?: string;
  };

  private headerContextMenuBlocker: ((e: MouseEvent) => void) | null = null;
  private lastClickY: number | null = null;
  private lastClickX: number | null = null;
  private hasActiveRange = false;
  private saveGridStateTimer: ReturnType<typeof setTimeout> | null = null;
  private totalRowCount = 0;
  // Incremented each time a new data fetch starts so in-flight responses can
  // detect they've been superseded (e.g., mode switch during load).
  private dataFetchGeneration = 0;
  // Holds AG Grid state to apply after Advanced column defs + row data finish loading.
  private pendingRestoreState: ReturnType<GridApi["getState"]> | null = null;

  connect() {
    super.connect(); // license key + outside click handler

    const hasAdvanced = this.advancedColumnDefsValue.length > 0;
    const sideBarConfig = this.buildSideBarConfig(hasAdvanced);

    const options: GridOptions = {
      theme: ebTheme,
      columnDefs: hasAdvanced ? this.basicColDefs(this.columnDefsValue) : this.columnDefsValue,
      sideBar: sideBarConfig,
      enableFilterHandlers: true,
      autoGroupColumnDef: {
        comparator: (valueA: unknown, valueB: unknown, nodeA: IRowNode, nodeB: IRowNode) => {
          const field = nodeA.field ?? "";
          const colDef = field ? this.gridApi?.getColumn(field)?.getColDef() : null;
          if ((colDef?.context as Record<string, unknown>)?.["countSort"]) {
            return (nodeA.allLeafChildren?.length ?? 0) - (nodeB.allLeafChildren?.length ?? 0);
          }
          return String(valueA ?? "").localeCompare(String(valueB ?? ""));
        },
      },
      components: {
        statusCellRenderer: StatusCellRenderer,
        actionsCellRenderer: ActionsCellRenderer,
      },
      onSelectionChanged: (event: SelectionChangedEvent) => {
        this.handleSelectionChanged(event);
      },
      onCellSelectionChanged: () => {
        this.handleRangeSelectionChanged();
      },
      onColumnRowGroupChanged: () => {
        this.autoSizeNonFlexColumns();
      },
      onRowGroupOpened: () => {
        this.autoSizeNonFlexColumns();
      },
      onModelUpdated: () => {
        if (this.pendingAutoSize) {
          this.pendingAutoSize = false;
          this.autoSizeNonFlexColumns();
        }
        this.refreshFooterRow();
      },
      onStateUpdated: () => {
        this.scheduleSaveGridState();
      },
      processCellForClipboard: (params: ProcessCellForExportParams) => formatCellValue(params),
      ...this.gridOptionsValue,
    };

    const rowSel = options.rowSelection as Record<string, unknown> | undefined;
    if (rowSel && typeof rowSel === "object") {
      rowSel.isRowSelectable = this.rowSelectableCallback;
    }

    if (!this.hasGridTarget) return;

    this.gridApi = createGrid(this.gridTarget, options);

    const isAutoHeight = this.gridOptionsValue?.domLayout === "autoHeight";
    if (!isAutoHeight) {
      this.initDynamicHeight();
    }

    this.loadGridState();

    if (this.followSelectionValue) {
      if (this.hasActionsMenuTarget) {
        this.actionsMenuTarget.classList.add("ag-grid-component-actions-menu--follow");
      }
      this.gridTarget.addEventListener("mousedown", (e: MouseEvent) => {
        this.lastClickY = e.clientY;
        this.lastClickX = e.clientX;
      });
    }

    if (hasAdvanced) {
      this.isBasicMode = true;
      this.setBasicModeColumnOptions();
      this.headerContextMenuBlocker = (e: MouseEvent) => {
        if (this.isBasicMode && (e.target as HTMLElement).closest(".ag-header")) {
          e.stopImmediatePropagation();
          e.preventDefault();
        }
      };
      this.gridTarget.addEventListener("contextmenu", this.headerContextMenuBlocker, true);
    }

    if (this.rowDataUrlValue) {
      this.fetchRowData(this.rowDataUrlValue);
    } else if (this.rowDataValue.length > 0) {
      this.pendingAutoSize = true;
      this.gridApi.setGridOption("rowData", this.rowDataValue);
    }

    this.gridBlurHandler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (this.gridTarget.contains(target)) return;
      const exportWrapper = this.hasExportToggleTarget
        ? this.exportToggleTarget.closest(".ag-grid-component-export-wrapper")
        : null;
      if (exportWrapper?.contains(target)) return;
      this.gridApi?.clearCellSelection();
      this.hasActiveRange = false;
    };
    document.addEventListener("mousedown", this.gridBlurHandler);
  }

  disconnect() {
    // Clean up CSRM-only state before base destroys the grid
    if (this.saveGridStateTimer !== null) {
      clearTimeout(this.saveGridStateTimer);
      this.saveGridStateTimer = null;
    }
    this.pendingRestoreState = null;
    this.isRestoringState = false;
    if (this.headerContextMenuBlocker && this.hasGridTarget) {
      this.gridTarget.removeEventListener("contextmenu", this.headerContextMenuBlocker, true);
      this.headerContextMenuBlocker = null;
    }
    super.disconnect(); // resize/click/mousedown listeners + gridApi.destroy()
  }

  onFilterTextBoxChanged() {
    if (!this.gridApi || !this.hasQuickFilterInputTarget) return;
    this.gridApi.setGridOption("quickFilterText", this.quickFilterInputTarget.value);
  }

  switchToBasic() {
    if (!this.gridApi) return;
    this.isBasicMode = true;
    this.gridApi.setGridOption("loading", true);
    this.setActiveToggle("basic");
    this.setBasicModeColumnOptions();
    if (this.hasFilterToggleTarget) {
      this.gridApi.closeToolPanel();
      this.filterToggleTarget.style.display = "none";
      this.filterToggleTarget.setAttribute("aria-pressed", "false");
    }
    if (this.hasColumnsToggleTarget) {
      this.columnsToggleTarget.style.display = "none";
      this.columnsToggleTarget.setAttribute("aria-pressed", "false");
    }
    if (this.hasExportToggleTarget) {
      this.closeExportDropdown();
    }
    this.hasActiveRange = false;
    this.hasRowSelection = false;
    this.gridApi?.clearCellSelection();
    this.fetchRowDataWithColumnDefs(this.rowDataUrlValue, this.basicColDefs(this.columnDefsValue));
  }

  switchToAdvanced() {
    if (!this.gridApi) return;
    this.isBasicMode = false;
    this.gridApi.setGridOption("loading", true);
    this.setActiveToggle("advanced");
    this.setAdvancedModeColumnOptions();
    if (this.hasFilterToggleTarget) {
      this.filterToggleTarget.style.display = "";
    }
    if (this.hasColumnsToggleTarget) {
      this.columnsToggleTarget.style.display = "";
    }
    this.hasActiveRange = false;
    this.hasRowSelection = false;
    this.gridApi?.clearCellSelection();
    this.fetchRowDataWithColumnDefs(this.advancedRowDataUrlValue, this.advancedColumnDefsValue);
  }

  exportCsv() {
    if (this.hasActiveRange) {
      this.exportRangeData("csv");
      return;
    }
    const processCellCallback = (params: ProcessCellForExportParams) => formatCellValue(params);
    if (this.hasRowSelection) {
      this.gridApi?.exportDataAsCsv({ onlySelected: true, processCellCallback });
    } else {
      this.gridApi?.exportDataAsCsv({ processCellCallback });
    }
    this.closeExportDropdown();
  }

  exportExcel() {
    if (this.hasActiveRange) {
      this.exportRangeData("excel");
      return;
    }
    const processCellCallback = (params: ProcessCellForExportParams) => formatCellValue(params);
    if (this.hasRowSelection) {
      this.gridApi?.exportDataAsExcel({ onlySelected: true, processCellCallback });
    } else {
      this.gridApi?.exportDataAsExcel({ processCellCallback });
    }
    this.closeExportDropdown();
  }

  fetchQuestionData(url: string): void {
    if (!this.gridApi) return;
    this.gridApi.setGridOption("loading", true);
    this.hasActiveRange = false;
    this.hasRowSelection = false;
    this.gridApi.clearCellSelection();
    fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to fetch question data: ${response.status}`);
        return response.json();
      })
      .then((json) => {
        if (!this.gridApi) return;
        this.pendingAutoSize = true;
        this.gridApi.setGridOption("columnDefs", json.column_defs ?? []);
        this.gridApi.setGridOption("rowData", json.rows ?? []);
        this.gridApi.setGridOption("pinnedBottomRowData", json.footer_row ?? []);
        this.gridApi.setGridOption("loading", false);
      })
      .catch((error) => {
        console.error("[AgGridComponent] fetchQuestionData error:", error);
        this.gridApi?.setGridOption("loading", false);
      });
  }

  private setBasicModeColumnOptions() {
    if (!this.gridApi) return;
    const baseColDef = (this.gridOptionsValue.defaultColDef as Record<string, unknown>) ?? {};
    this.gridApi.setGridOption("defaultColDef", { ...baseColDef, suppressHeaderMenuButton: true });
    this.gridApi.setGridOption("suppressDragLeaveHidesColumns", true);
    if (Object.keys(this.basicModeOptionsValue).length > 0) {
      this.gridApi.updateGridOptions(this.basicModeOptionsValue);
    }
  }

  private setAdvancedModeColumnOptions() {
    if (!this.gridApi) return;
    const baseColDef = (this.gridOptionsValue.defaultColDef as Record<string, unknown>) ?? {};
    this.gridApi.setGridOption("defaultColDef", { ...baseColDef });
    this.gridApi.setGridOption("suppressDragLeaveHidesColumns", false);
    if (Object.keys(this.advancedModeOptionsValue).length > 0) {
      this.gridApi.updateGridOptions(this.advancedModeOptionsValue);
    }
  }

  private handleRangeSelectionChanged() {
    const ranges = this.gridApi?.getCellRanges();
    if (!ranges || ranges.length === 0) {
      this.hasActiveRange = false;
      return;
    }
    const range = ranges[0];
    const startRow = range.startRow?.rowIndex ?? 0;
    const endRow = range.endRow?.rowIndex ?? 0;
    const rowSpan = Math.abs(endRow - startRow) + 1;
    this.hasActiveRange = rowSpan > 1 || range.columns.length > 1;
  }

  private exportRangeData(format: "csv" | "excel") {
    if (!this.gridApi) return;
    const ranges = this.gridApi.getCellRanges();
    if (!ranges || ranges.length === 0) return;
    const range = ranges[0];
    const startIsPinned = range.startRow!.rowPinned === "bottom";
    const endIsPinned = range.endRow!.rowPinned === "bottom";
    const includeFooter = startIsPinned || endIsPinned;
    const columnKeys = range.columns.map((col) => col.getColId());
    const processCellCallback = (params: ProcessCellForExportParams) => formatCellValue(params);

    let startRow: number;
    let endRow: number;

    if (startIsPinned && endIsPinned) {
      startRow = 0;
      endRow = -1;
    } else if (endIsPinned) {
      startRow = range.startRow!.rowIndex;
      endRow = this.gridApi.getDisplayedRowCount() - 1;
    } else if (startIsPinned) {
      startRow = 0;
      endRow = range.endRow!.rowIndex;
    } else {
      const rawStart = range.startRow!.rowIndex;
      const rawEnd = range.endRow!.rowIndex;
      startRow = Math.min(rawStart, rawEnd);
      endRow = Math.max(rawStart, rawEnd);
    }

    const params = {
      columnKeys,
      processCellCallback,
      shouldRowBeSkipped: ({ node }: { node: IRowNode }) => {
        if (node.rowPinned === "bottom") return !includeFooter;
        const idx = node.rowIndex ?? -1;
        return idx < startRow || idx > endRow;
      },
    };
    if (format === "csv") {
      this.gridApi.exportDataAsCsv(params);
    } else {
      this.gridApi.exportDataAsExcel(params);
    }
    this.closeExportDropdown();
  }

  private loadGridState() {
    if (!this.gridStateUrlValue || !this.gridApi) return;
    fetch(this.gridStateUrlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) return;
        return response.json();
      })
      .then((json) => {
        if (!json?.state || !this.gridApi) return;
        const { mode, ...agState } = json.state as Record<string, unknown>;
        if (Object.keys(agState).length === 0) return;
        const hasAdvanced = this.advancedColumnDefsValue.length > 0;
        this.isRestoringState = true;
        if (mode === "advanced" && hasAdvanced) {
          this.pendingRestoreState = agState as ReturnType<GridApi["getState"]>;
          this.switchToAdvanced();
        } else {
          this.gridApi.setState(agState as ReturnType<GridApi["getState"]>);
          requestAnimationFrame(() => {
            this.isRestoringState = false;
            this.syncPanelButtonStates();
          });
        }
      })
      .catch(() => {
        // Non-critical — grid functions without saved state
      });
  }

  private applyPendingRestoreState() {
    if (!this.pendingRestoreState || !this.gridApi) return;
    const state = this.pendingRestoreState;
    this.pendingRestoreState = null;
    this.gridApi.setState(state);
    requestAnimationFrame(() => {
      this.isRestoringState = false;
      this.syncPanelButtonStates();
    });
  }

  private scheduleSaveGridState() {
    if (!this.gridStateUrlValue || this.isRestoringState) return;
    if (this.saveGridStateTimer !== null) {
      clearTimeout(this.saveGridStateTimer);
    }
    this.saveGridStateTimer = setTimeout(() => {
      this.saveGridStateTimer = null;
      this.persistGridState();
    }, 2000);
  }

  private persistGridState() {
    if (!this.gridStateUrlValue || !this.gridApi) return;
    const state = {
      ...this.gridApi.getState(),
      mode: this.isBasicMode ? "basic" : "advanced",
    };
    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
    fetch(this.gridStateUrlValue, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      credentials: "same-origin",
      body: JSON.stringify({ state }),
    }).catch(() => {
      // Non-critical — state save failure is silent
    });
  }

  private fetchRowData(url: string) {
    this.fetchRowDataWithColumnDefs(url, null);
  }

  private fetchRowDataWithColumnDefs(url: string, columnDefs: ColDef[] | null) {
    const generation = ++this.dataFetchGeneration;
    fetch(url, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch row data: ${response.status}`);
        }
        return response.json();
      })
      .then((json) => {
        if (generation !== this.dataFetchGeneration || !this.gridApi) return;
        const rows = Array.isArray(json) ? json : (json.rows ?? []);
        if (columnDefs !== null) {
          this.gridApi.setGridOption("columnDefs", columnDefs);
        } else if (!Array.isArray(json) && json.column_defs) {
          this.gridApi.setGridOption("columnDefs", json.column_defs);
        }
        this.totalRowCount = rows.length;
        this.pendingAutoSize = true;
        this.gridApi.setGridOption("rowData", rows);
        const hasFooterConfig = !!(this.footerCountsValue.labelField || this.footerCountsValue.sumFields?.length);
        if (!hasFooterConfig) {
          this.gridApi.setGridOption(
            "pinnedBottomRowData",
            Array.isArray(json) ? [] : (json.footer_row ?? [])
          );
        }
        this.gridApi.setGridOption("loading", false);
        this.applyPendingRestoreState();
      })
      .catch((error) => {
        if (generation !== this.dataFetchGeneration) return;
        console.error("[AgGridComponent] fetchRowData error:", error);
        this.gridApi?.setGridOption("loading", false);
        this.pendingRestoreState = null;
        this.isRestoringState = false;
      });
  }

  private handleSelectionChanged(event: SelectionChangedEvent) {
    const selectedRows = event.api.getSelectedRows() as Array<Record<string, unknown>>;
    const count = selectedRows.length;
    this.hasRowSelection = count > 0;

    if (!this.hasActionsMenuTarget) return;

    if (count > 0) {
      this.actionsMenuTarget.classList.add("visible");
      if (this.hasSelectedCountTarget) {
        this.selectedCountTarget.textContent = `${count} selected`;
      }
      this.renderActions(selectedRows);
      if (this.followSelectionValue) {
        this.positionMenuAtSelection();
      }
    } else {
      this.actionsMenuTarget.classList.remove("visible");
      if (this.hasSelectedCountTarget) {
        this.selectedCountTarget.textContent = "";
      }
      this.clearActions();
      if (this.followSelectionValue) {
        const menu = this.actionsMenuTarget;
        menu.style.top = "";
        menu.style.left = "";
        menu.style.right = "";
        menu.style.bottom = "";
      }
    }
  }

  private positionMenuAtSelection(): void {
    if (!this.gridApi) return;
    const selectedNodes = this.gridApi.getSelectedNodes();
    if (selectedNodes.length === 0) return;

    const lastNode = selectedNodes[selectedNodes.length - 1];
    const rowEl = this.gridTarget.querySelector(
      `[row-id="${CSS.escape(String(lastNode.id))}"]`
    ) as HTMLElement | null;

    let clientY = this.lastClickY ?? 0;
    if (rowEl) {
      const rect = rowEl.getBoundingClientRect();
      clientY = rect.top + rect.height / 2;
    }

    this.positionMenuNearClick(clientY, this.lastClickX ?? 0);
  }

  private refreshFooterRow(): void {
    if (!this.gridApi) return;
    const config = this.footerCountsValue;
    if (!config.labelField && !config.sumFields?.length) return;

    const displayedCount = this.gridApi.getDisplayedRowCount();
    const footerRow: Record<string, unknown> = {};

    if (config.labelField) {
      const label = config.countLabel ?? "rows";
      footerRow[config.labelField] =
        displayedCount === this.totalRowCount
          ? `${this.totalRowCount} ${label}`
          : `${displayedCount} of ${this.totalRowCount} ${label}`;
    }

    if (config.sumFields && config.sumFields.length > 0) {
      const sums: Record<string, number> = {};
      config.sumFields.forEach((f: string) => { sums[f] = 0; });
      this.gridApi.forEachNodeAfterFilter((node) => {
        if (!node.data) return;
        config.sumFields!.forEach((f: string) => {
          sums[f] += Number((node.data as Record<string, unknown>)[f] ?? 0);
        });
      });
      Object.assign(footerRow, sums);
    }

    this.gridApi.setGridOption("pinnedBottomRowData", [footerRow]);
  }

  private positionMenuNearClick(clientY: number, clientX: number): void {
    const menu = this.actionsMenuTarget;
    const wrapper = menu.parentElement;
    if (!wrapper) return;

    const menuWidth = menu.offsetWidth || 275;
    const menuHeight = menu.offsetHeight || 220;

    const wrapperRect = wrapper.getBoundingClientRect();
    const selectionColWidth = this.gridApi?.getAllDisplayedColumns()[0]?.getActualWidth() ?? 48;

    const maxTop = window.innerHeight - menuHeight - 16;
    const top = Math.min(Math.max(clientY + 8, 0), Math.max(maxTop, 0));

    const minLeft = wrapperRect.left + selectionColWidth + 8;
    const maxLeft = window.innerWidth - menuWidth - 8;
    const left = Math.min(Math.max(clientX + 8, minLeft), Math.max(maxLeft, minLeft));

    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
    menu.style.right = "auto";
    menu.style.bottom = "auto";
  }
}
